<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import type { BookPageConfig } from '../configs/types'

interface Props {
  book: any
  config: BookPageConfig
  coverUrl?: string
  popupTOC: boolean
  bannerShow: boolean
  currentProgressChapterId?: string
  unlockedChapterIds?: string[]
  isCompleted?: boolean
  isInShelf?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (event: 'update:popup-t-o-c', value: boolean): void
  (event: 'goto-tags', tagId: string): void
  (event: 'open-toc'): void
  (event: 'bookshelf'): void
  (event: 'read'): void
  (event: 'back'): void
  (event: 'cover-loaded'): void
  (event: 'preload-complete', costMs: number): void
  (event: 'reset-confirm', chapterId: string | number | undefined, clearRoleConfig: boolean): void
}>()

// 创建可写的 computed 用于 v-model 双向绑定
const popupTOCModel = computed({
  get: () => props.popupTOC,
  set: value => emit('update:popup-t-o-c', value),
})

// 目录重置模式状态
const restartMode = ref(false)
const confirmVisible = ref(false)
const resetChapterId = ref<string | number | undefined>(undefined)
const resetChapterNo = ref<number | undefined>(undefined)

function openTOC() {
  restartMode.value = false
  popupTOCModel.value = true
}

function openRestart() {
  restartMode.value = true
  popupTOCModel.value = true
}

function onResetChapter(chapterId: string | number, chapterNo: number) {
  popupTOCModel.value = false
  restartMode.value = false
  resetChapterId.value = chapterId
  resetChapterNo.value = chapterNo
  confirmVisible.value = true
}

function onResetAll() {
  popupTOCModel.value = false
  restartMode.value = false
  resetChapterId.value = undefined
  confirmVisible.value = true
}

function handleResetConfirm(chapterId: string | number | undefined, clearRoleConfig: boolean) {
  emit('reset-confirm', chapterId, clearRoleConfig)
}

const rootRef = ref<HTMLElement>()
// 封面图实际高宽比（naturalHeight / naturalWidth），加载完成后写入
const coverRatio = ref(0)

const bgStyle = computed(() => {
  if (!props.coverUrl)
    return ''
  let style = `background: url(${props.coverUrl}) no-repeat; background-size: 100% auto; background-position: center top;`
  // 底缘渐隐锚定封面实际下缘（WGSQ-474）：长封面延伸到屏幕底部、信息区背后保留画面，
  // 短封面在图片下缘前收尾，均不会出现硬切边
  if (coverRatio.value) {
    const w = rootRef.value?.clientWidth || window.innerWidth
    const h = rootRef.value?.clientHeight || window.innerHeight
    const bottom = Math.min((w * coverRatio.value) / h, 1) * 100
    const solidEnd = Math.max(bottom - 32, 0).toFixed(1)
    const midEnd = Math.max(bottom - 12, 0).toFixed(1)
    const grad = `linear-gradient(to bottom, #000 ${solidEnd}%, rgba(0, 0, 0, 0.55) ${midEnd}%, transparent ${(bottom + 2).toFixed(1)}%)`
    style += ` mask-image: ${grad}; -webkit-mask-image: ${grad};`
  }
  return style
})

// 阅读进度命中的章节序号（1-based）；无阅读记录返回 0（WGSQ-473）
const progressChapterNo = computed(() => {
  if (!props.currentProgressChapterId)
    return 0
  const chapters = props.book?.chapters || []
  const idx = chapters.findIndex((c: any) => String(c.chapterId) === String(props.currentProgressChapterId))
  return idx === -1 ? 0 : idx + 1
})

// 主按钮文案三态（WGSQ-473）：完读 Play Again / 有记录 Continue Ch.{n} / 无记录 Play
const playText = computed(() => {
  if (props.isCompleted)
    return 'Play Again'
  return progressChapterNo.value ? `Continue Ch.${progressChapterNo.value}` : 'Play'
})

// 当前章节
const currentChapter = computed(() => {
  const chapters = props.book?.chapters || []
  if (!chapters.length)
    return null
  const index = chapters.findIndex((c: any) => c.chapterId === props.book?.currentChapterId)
  const chapter = index !== -1 ? chapters[index] : chapters[0]
  return { ...chapter, index: index !== -1 ? index : 0 }
})

// 预加载 Unity WebGL 资源文件，各资源并发加载、各自立即缓存
// baseUrl 使用 fetch response.url（跟随重定向后的实际地址），确保解析出的绝对 URL 与 Unity Loader 一致
function preloadUnityAssets(baseUrl: string, paths: string[]): Promise<PromiseSettledResult<Response>[]> {
  return Promise.allSettled(
    paths.map((relativePath) => {
      const absoluteUrl = new URL(relativePath, baseUrl).href
      return fetch(absoluteUrl, { cache: 'force-cache' })
    }),
  )
}

onMounted(async () => {
  const preloadStart = performance.now()
  try {
    const response = await fetch('/content/read/index.html')
    if (!response.ok)
      return
    const htmlContent = await response.text()
    const unityFiles = htmlContent.match(/[^"]+?\.unityweb/g) || []
    if (unityFiles.length) {
      const results = await preloadUnityAssets(response.url, unityFiles)
      const allSucceeded = results.every(r => r.status === 'fulfilled' && r.value.ok)
      if (allSucceeded) {
        const costMs = Math.round(performance.now() - preloadStart)
        emit('preload-complete', costMs)
      }
    }
  }
  catch {
    // 预加载失败不影响主流程
  }
})

onMounted(() => {
  if (props.coverUrl) {
    const img = new Image()
    img.onload = () => {
      coverRatio.value = img.naturalWidth ? img.naturalHeight / img.naturalWidth : 0
      emit('cover-loaded')
    }
    img.src = props.coverUrl
  }
})
</script>

<template>
  <div ref="rootRef" class="h-full relative overflow-hidden bg-n-bg-global">
    <div
      class="absolute top-12px left-12px z-10 cursor-pointer"
      @click="emit('back')"
    >
      <i-icon-park-outline-left class="text-24px text-white" />
    </div>

    <div class="book-bg absolute inset-0" :style="bgStyle" />
    <div class="book-mask absolute bottom-0 w-full z-10" />

    <!-- TOC 弹窗 -->
    <BizPopupInteractiveTOC
      v-if="book"
      v-model="popupTOCModel"
      :book="book"
      :lock-icon-color="config.theme.tocLockColor"
      :restart-mode="restartMode"
      :current-chapter-id="props.currentProgressChapterId"
      :unlocked-chapter-ids="props.unlockedChapterIds"
      @reset-chapter="onResetChapter"
      @reset-all="onResetAll"
    />

    <!-- 重置二次确认弹窗 -->
    <BizDialogResetConfirm
      v-model="confirmVisible"
      :chapter-id="resetChapterId"
      :chapter-no="resetChapterNo"
      @confirm="handleResetConfirm"
    />

    <BasicFixedBottom>
      <div class="px-24px pb-24px">
        <!-- 元信息行：章节进度 | 连载状态（CG 徽章待接口补字段后加） -->
        <div class="flex items-center text-12px font-300 color-#CFD0D6">
          <span class="shrink-0">Chapter {{ currentChapter?.index + 1 }}/{{ props.book.chapters.length }}</span>
          <span class="mx-8px color-#676566 shrink-0">|</span>
          <span class="shrink-0">{{ props.book.finishStatus === 1 ? 'Completed' : 'Ongoing' }}</span>
        </div>
        <!-- 书名：衬线斜体（对照蓝湖） -->
        <h3 class="book-title mt-8px color-white">
          {{ props.book.contentName }}
        </h3>
        <!-- 章节行：当前章节名 + 时长 -->
        <div class="flex justify-between items-center mt-10px color-#CFD0D6 text-12px font-300">
          <p class="truncate min-w-0 pr-12px">
            Chapter {{ currentChapter?.index + 1 }} · {{ currentChapter?.chapterName }}
          </p>
          <p v-if="currentChapter?.needTime" class="shrink-0 flex items-center">
            <img src="/images/vowpath/book/icon-duration.png" class="w-12px h-12px mr-4px">
            {{ currentChapter.needTime }} min
          </p>
        </div>
        <div class="chapter-brief max-h-138px overflow-y-auto mt-8px">
          <p class="lh-18px text-12px color-#CFD0D6 font-300">
            {{ currentChapter?.chapterBrief }}
          </p>
        </div>
        <div v-if="props.book.tagList?.length" class="flex flex-wrap text-12px font-300 color-#fff mt-10px mb--8px">
          <span v-for="tag in props.book.tagList" :key="tag.name" class="px-9px py-3px b-rd-16px bg-[rgba(198,201,236,0.2)] mr-8px mb-8px" @click="emit('goto-tags', tag.id)">
            {{ tag.name }}
          </span>
        </div>
      </div>
      <div class="flex w-full px-24px pt-12px pb-2 items-center">
        <span class="icon-btn relative" @click="openRestart">
          <img src="/images/vowpath/book/btn-reset.png" class="w-45px h-45px">
          <!-- 完读态气泡：指向重置按钮（WGSQ-473，对照蓝湖「不同按钮状态」） -->
          <span v-if="props.isCompleted" class="replay-tip">Start to Replay</span>
        </span>
        <span class="icon-btn shelf-btn ml-16px" @click="emit('bookshelf')">
          <img
            :src="props.isInShelf ? '/images/vowpath/book/icon-heart-active.svg' : '/images/vowpath/book/icon-heart.svg'"
            class="w-24px h-21px"
          >
        </span>
        <a
          class="start-reading icon-btn flex-1 ml-16px block h-45px lh-45px text-center text-15px font-bold rounded-30px cursor-pointer btn-solid text-white"
          :class="{ 'is-replay': props.isCompleted }"
          @click="emit('read')"
        >
          {{ playText }}
        </a>
      </div>
    </BasicFixedBottom>
  </div>
</template>

<style lang="scss" scoped>
/* 底部蒙层：仅文字区域一层接近透明的暗化渐变，保证白字可读、画面保持透出 */
.book-mask {
  height: 45%;
  background: linear-gradient(rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.2) 35%, rgba(0, 0, 0, 0.38) 100%);
  pointer-events: none;
}
/* 书名：衬线斜体大标题（对照蓝湖，系统衬线字体近似还原） */
.book-title {
  font-family: Georgia, 'Times New Roman', serif;
  font-style: italic;
  font-size: 26px;
  line-height: 32px;
  font-weight: 700;
}
.start-reading {
  background-image: linear-gradient(270deg, rgba(255, 90, 205, 1) 0%, rgba(154, 87, 255, 1) 100%);

  /* 完读态 Play Again：降饱和压暗渐变 + 半透明文字（对照蓝湖「不同按钮状态」） */
  &.is-replay {
    filter: saturate(0.5) brightness(0.72);
    color: rgba(255, 255, 255, 0.6);
  }
}

/* 完读态「Start to Replay」气泡：背景为蓝湖切图（113x28 胶囊+小尾巴，渐变已含在图内），
   胶囊主体高 22px、下方尾巴 6px，padding-bottom 让文字在胶囊部分垂直居中 */
.replay-tip {
  position: absolute;
  left: 0;
  bottom: calc(100% + 4px);
  width: 113px;
  height: 28px;
  padding-bottom: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: url('/images/vowpath/book/tip-replay.svg') no-repeat center / 100% 100%;
  color: #ffffff;
  font-size: 12px;
  font-weight: 400;
  white-space: nowrap;
  z-index: 1;
}
.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.15s;
  &:active {
    opacity: 0.6;
    transform: scale(0.9);
  }
}
/* 收藏圆钮：CSS 圆底 + SVG 心形（对照蓝湖 45px 圆、深色半透明底细描边） */
.shelf-btn {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  border: 0.5px solid rgba(255, 255, 255, 0.12);
}
.chapter-brief {
  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
  }
}
</style>
